import { enqueueTelemetryEvents, getTelemetryClientConfig } from '@ms/yammer-telemetry-store';

import type { InfoEvent, LogEventInput } from '../events';

export type DeliveryLifecycleEventInputType = 'Analytics' | 'AnalyticsV2' | 'Log';

export type DeliveryLifecycleOutcome =
  | 'attempted'
  | 'auth_unavailable'
  | 'fetch_failed'
  | 'invalid_client_id'
  | 'non_retryable_status'
  | 'pagehide_queue_observed'
  | 'sent'
  | 'retryable_status'
  | 'telemetry_host_unavailable';

export type DeliveryLifecycleErrorType =
  | 'AbortError'
  | 'Error'
  | 'NetworkError'
  | 'TimeoutError'
  | 'TypeError'
  | 'other';

export const TelemetryDeliveryLifecycleEventName = 'telemetry_delivery_lifecycle';

type CreateDeliveryLifecycleCorrelationId = () => string;
export const createDeliveryLifecycleCorrelationId: CreateDeliveryLifecycleCorrelationId = () => crypto.randomUUID();

interface ReportDeliveryLifecycleInfoOptions {
  readonly correlationId?: string;
  readonly errorType?: DeliveryLifecycleErrorType;
  readonly eventInputType: DeliveryLifecycleEventInputType;
  readonly outcome: DeliveryLifecycleOutcome;
  readonly responseCode?: number;
  readonly resultCount?: number;
  readonly skipAuthentication?: boolean;
}

type IsDeliveryLifecycleLogEvent = (event: LogEventInput) => boolean;
export const isDeliveryLifecycleLogEvent: IsDeliveryLifecycleLogEvent = ({ EventName }) =>
  EventName === TelemetryDeliveryLifecycleEventName;

type HasDeliveryLifecycleLogEvent = (events: LogEventInput[]) => boolean;
export const hasDeliveryLifecycleLogEvent: HasDeliveryLifecycleLogEvent = (events) =>
  events.some(isDeliveryLifecycleLogEvent);

type GetReportableDeliveryLifecycleLogEventCount = (events: LogEventInput[]) => number;
export const getReportableDeliveryLifecycleLogEventCount: GetReportableDeliveryLifecycleLogEventCount = (events) =>
  events.filter((event) => !isDeliveryLifecycleLogEvent(event)).length;

type ShouldReportDeliveryLifecycleForLogEvents = (events: LogEventInput[]) => boolean;
export const shouldReportDeliveryLifecycleForLogEvents: ShouldReportDeliveryLifecycleForLogEvents = (events) =>
  getReportableDeliveryLifecycleLogEventCount(events) > 0;

export const getDeliveryLifecycleOutcomeForResponseStatus = (responseStatus: number): DeliveryLifecycleOutcome => {
  if (responseStatus === 408 || responseStatus === 429 || responseStatus >= 500) {
    return 'retryable_status';
  }

  return 'non_retryable_status';
};

type GetDeliveryLifecycleErrorType = (error: unknown) => DeliveryLifecycleErrorType;
export const getDeliveryLifecycleErrorType: GetDeliveryLifecycleErrorType = (error) => {
  const errorName = error instanceof Error ? error.name : undefined;

  switch (errorName) {
    case 'AbortError':
      return 'AbortError';
    case 'Error':
      return 'Error';
    case 'NetworkError':
      return 'NetworkError';
    case 'TimeoutError':
      return 'TimeoutError';
    case 'TypeError':
      return 'TypeError';
    default:
      return 'other';
  }
};

type CreateDeliveryLifecycleInfoEvent = (options: ReportDeliveryLifecycleInfoOptions) => InfoEvent;
export const createDeliveryLifecycleInfoEvent: CreateDeliveryLifecycleInfoEvent = ({
  correlationId,
  errorType,
  eventInputType,
  outcome,
  responseCode,
  resultCount,
  skipAuthentication,
}: ReportDeliveryLifecycleInfoOptions) => ({
  type: 'Info',
  name: TelemetryDeliveryLifecycleEventName,
  occurredAt: Date.now().toString(),
  skipAuthentication,
  properties: {
    type: eventInputType,
    ...(correlationId && { correlationId }),
    outcome,
    ...(errorType && { errorType }),
    ...(responseCode != null && { responseCode }),
    ...(resultCount != null && { resultCount }),
  },
});

type ReportDeliveryLifecycleInfo = (options: ReportDeliveryLifecycleInfoOptions) => void;
export const reportDeliveryLifecycleInfo: ReportDeliveryLifecycleInfo = (options) => {
  if (!getTelemetryClientConfig()) {
    return;
  }

  enqueueTelemetryEvents<InfoEvent>('Info', [createDeliveryLifecycleInfoEvent(options)]);
};
